import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import {
  notificationService,
  NotificationDTO,
  renderNotificationText,
} from '../services/notificationService';
import './NotificationBell.css';

const POLL_INTERVAL_MS = 45_000;

/** Header bell: polls the unread count and shows the latest notifications. */
const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const refreshCount = useCallback(async () => {
    try {
      const response = await notificationService.getUnreadCount();
      setUnreadCount(response.data.data?.count ?? 0);
    } catch {
      // polling failures are silent — next tick retries
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [refreshCount]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      try {
        const response = await notificationService.getMyNotifications(1, 10);
        setItems(response.data.data?.data ?? []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleItemClick = async (item: NotificationDTO) => {
    setOpen(false);
    if (!item.isRead) {
      try {
        await notificationService.markAsRead(item.id);
        setUnreadCount((count) => Math.max(0, count - 1));
      } catch {
        // navigation still proceeds
      }
    }
    if (item.linkUrl) navigate(item.linkUrl);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setUnreadCount(0);
      setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch {
      // leave state unchanged on failure
    }
  };

  const formatTime = (iso: string) => {
    const langCode = i18n.language === 'ja' ? 'ja-JP' : 'en-US';
    return new Date(iso).toLocaleString(langCode, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="notification-bell" ref={containerRef}>
      <button
        type="button"
        className="notification-bell-btn"
        onClick={toggleOpen}
        title={t('notifications.title')}
        aria-label={t('notifications.title')}
      >
        <Bell size={18} strokeWidth={2} />
        {unreadCount > 0 && (
          <span className="notification-bell-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <span>{t('notifications.title')}</span>
            {unreadCount > 0 && (
              <button type="button" onClick={handleMarkAllRead}>
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className="notification-dropdown-list">
            {loading && <div className="notification-dropdown-empty">{t('common.loading')}</div>}
            {!loading && items.length === 0 && (
              <div className="notification-dropdown-empty">{t('notifications.empty')}</div>
            )}
            {!loading &&
              items.map((item) => {
                const text = renderNotificationText(item, t);
                return (
                  <button
                    type="button"
                    key={item.id}
                    className={`notification-item ${item.isRead ? '' : 'unread'}`}
                    onClick={() => handleItemClick(item)}
                  >
                    <span className={`notification-item-dot severity-${item.severity}`} />
                    <span className="notification-item-body">
                      <p className="notification-item-title">{text.title}</p>
                      <p className="notification-item-message">{text.message}</p>
                      <span className="notification-item-time">{formatTime(item.createdAt)}</span>
                    </span>
                  </button>
                );
              })}
          </div>

          <div className="notification-dropdown-footer">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
            >
              {t('notifications.viewAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
